import * as XLSX from 'xlsx';

/**
 * Spreadsheet parsing for the chat-driven migration wizard.
 *
 * Unlike the classic DataMigrationStep (which assumes the header is row 0),
 * we return the FULL raw grid with NO header assumption. The LLM inspects a
 * sample of this grid and decides which row holds the real headers — this is
 * what lets it handle exports like ERPNext "Data Import Template" whose first
 * rows are titles/notes and whose first data column is blank.
 */

export interface ParsedFile {
  filename: string;
  /** All rows, verbatim and trimmed. No header row assumed. */
  grid: string[][];
  rowCount: number;
}

const MAX_UPLOAD_BYTES = 50 * 1024 * 1024;

export async function parseSpreadsheet(file: File): Promise<ParsedFile> {
  if (file.size > MAX_UPLOAD_BYTES) {
    throw new Error(`File terlalu besar (${Math.round(file.size / 1024 / 1024)} MB). Maks 50 MB.`);
  }
  const name = file.name.toLowerCase();
  let grid: string[][];
  if (/\.(xlsx|xls)$/.test(name)) {
    const buf = await file.arrayBuffer();
    const wb = XLSX.read(buf, { type: 'array' });
    const first = wb.SheetNames[0];
    if (!first) throw new Error('Workbook tidak punya sheet.');
    const aoa = XLSX.utils.sheet_to_json<unknown[]>(wb.Sheets[first]!, { header: 1, raw: false, defval: '' });
    grid = aoa.map((r) => (r ?? []).map((c) => String(c ?? '').trim()));
  } else if (/\.(csv|tsv|txt)$/.test(name)) {
    grid = parseDelimitedGrid(await file.text());
  } else {
    throw new Error('Format tidak didukung. Gunakan CSV atau XLSX.');
  }
  // Drop fully-empty trailing rows so the sample + counts are meaningful.
  while (grid.length && grid[grid.length - 1]!.every((c) => c === '')) grid.pop();
  return { filename: file.name, grid, rowCount: grid.length };
}

export interface SlicedTable {
  headers: string[];
  dataRows: string[][];
}

/**
 * sliceTable cuts the raw grid at the LLM-chosen header/data rows. Headers are
 * the RAW (trimmed) header texts — they MUST match the keys in the model's
 * mapping, and they become row 0 of the wire payload the import endpoints read.
 */
export function sliceTable(grid: string[][], headerRow: number, dataStartRow: number): SlicedTable {
  const headers = (grid[headerRow] ?? []).map((h) => (h ?? '').trim());
  const width = headers.length;
  const dataRows = grid
    .slice(dataStartRow)
    .filter((r) => r.some((c) => (c ?? '').trim() !== ''))
    .map((r) => Array.from({ length: width }, (_, i) => (r[i] ?? '').trim()));
  return { headers, dataRows };
}

/** Wire shape for /admin/imports/* : row 0 = headers, rest = data. */
export function toWireRows(t: SlicedTable): string[][] {
  return [t.headers, ...t.dataRows];
}

/** First `n` rows of the grid, for the chat attachment sample sent to the model. */
export function gridSample(grid: string[][], n = 15): string[][] {
  return grid.slice(0, n).map((r) => r.map((c) => (c ?? '').trim()));
}

// parseDelimitedGrid is a minimal CSV/TSV reader: quoted fields, "" → "
// escape, CRLF tolerant. Mirrors the parser in DataMigrationStep but returns
// the full grid (string[][]) rather than {headers, rows}. Auto-detects tab vs
// comma from the first line.
function parseDelimitedGrid(text: string): string[][] {
  const firstLine = text.slice(0, text.indexOf('\n') === -1 ? text.length : text.indexOf('\n'));
  const delim = firstLine.includes('\t') && !firstLine.includes(',') ? '\t' : ',';
  const grid: string[][] = [];
  let cur: string[] = [];
  let field = '';
  let inQ = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (inQ) {
      if (c === '"' && text[i + 1] === '"') { field += '"'; i++; }
      else if (c === '"') { inQ = false; }
      else { field += c; }
    } else if (c === '"') {
      inQ = true;
    } else if (c === delim) {
      cur.push(field); field = '';
    } else if (c === '\n' || c === '\r') {
      if (c === '\r' && text[i + 1] === '\n') i++;
      cur.push(field); field = '';
      grid.push(cur.map((v) => v.trim()));
      cur = [];
    } else {
      field += c;
    }
  }
  if (field !== '' || cur.length > 0) {
    cur.push(field);
    grid.push(cur.map((v) => v.trim()));
  }
  return grid;
}
